export interface PublicTokenConfig {
  leadSourceId: number | null;
  stagePipelineId: number | null;
  stagePipelineIntentId: number | null;
  stagePipelineSignedId: number | null;
  stagePipelineQuotedId: number | null;
  campusId: number | null;
  agentId: string | null;
  signatureFormId: string | null;
  signatureFormIdEn: string | null;
  sendEmail: boolean;
  sendSms: boolean;
  requireIdentity: boolean;
  sourceDescription: string | null;
  phoneNumber?: string | null;
  whatsappNumber?: string | null;
  consentTrackingEventName: string;
  leadEventName: string | null;
  quoteEventName: string | null;
  gtmBaseUrl?: string | null;
  gtmIdEn?: string | null;
  gtmIdEs?: string | null;
  googleAdsIdEn?: string | null;
  googleAdsIdEs?: string | null;
  googleAdsPhoneEn?: string | null;
  googleAdsPhoneEs?: string | null;
  googleAdsLeadConversionIdEn?: string | null;
  googleAdsLeadConversionIdEs?: string | null;
  googleAdsConsentConversionIdEn?: string | null;
  googleAdsConsentConversionIdEs?: string | null;
  googleAdsQuoteConversionIdEn?: string | null;
  googleAdsQuoteConversionIdEs?: string | null;
}

const numberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const stringOrNull = (value: unknown): string | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  return String(value);
};

const boolOrDefault = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(normalized)) {
      return true;
    }
    if (["false", "0", "no", "n"].includes(normalized)) {
      return false;
    }
  }
  return fallback;
};

export const getPublicAccessToken = (): string => {
  const lang = window.location.pathname.split("/").filter(Boolean)[0] || "es";
  const envToken = lang === "en"
    ? process.env.REACT_APP_PUBLIC_GRAPHQL_TOKEN_EN
    : process.env.REACT_APP_PUBLIC_GRAPHQL_TOKEN_ES;

  return new URLSearchParams(window.location.search).get("token") ||
    localStorage.getItem("public_access_token") ||
    envToken ||
    process.env.REACT_APP_PUBLIC_GRAPHQL_TOKEN ||
    "";
};

export const decodePublicTokenConfig = (): PublicTokenConfig => {
  const env = process.env;

  // El token es un UUID opaco (WebQuoteConnection.token) o el token estático de dev —
  // nunca algo para decodificar client-side. Todo el contexto de la campaña
  // (lead_source/pipeline/campus/agente/flags + Google Ads/GTM de tracking) ya viene
  // resuelto por el backend en la query `publicConfig` y queda guardado acá por
  // `ConfigurationGate` (src/index.tsx) antes de que el wizard monte. Los env vars
  // `REACT_APP_PUBLIC_*` quedan solo como último fallback de conveniencia para
  // desarrollo local, por si `ConfigurationGate` todavía no corrió.
  const savedConfigRaw = localStorage.getItem("public_dynamic_config");
  const context: Record<string, unknown> = savedConfigRaw
    ? JSON.parse(savedConfigRaw)
    : {};

  return {
    leadSourceId:
      numberOrNull(context.leadSourceId) ??
      numberOrNull(env.REACT_APP_PUBLIC_LEAD_SOURCE_ID),
    stagePipelineId:
      numberOrNull(context.stagePipelineId) ??
      numberOrNull(env.REACT_APP_PUBLIC_STAGE_PIPELINE_ID),
    stagePipelineIntentId:
      numberOrNull(context.stagePipelineIntentId) ??
      numberOrNull(env.REACT_APP_PUBLIC_STAGE_PIPELINE_ID_INTENT),
    stagePipelineSignedId:
      numberOrNull(context.stagePipelineSignedId) ??
      numberOrNull(env.REACT_APP_PUBLIC_STAGE_PIPELINE_ID_SIGNED),
    stagePipelineQuotedId:
      numberOrNull(context.stagePipelineQuotedId) ??
      numberOrNull(env.REACT_APP_PUBLIC_STAGE_PIPELINE_ID_QUOTED),
    campusId:
      numberOrNull(context.campusId) ??
      numberOrNull(env.REACT_APP_PUBLIC_CAMPUS_ID),
    agentId:
      stringOrNull(context.agentId) ?? stringOrNull(env.REACT_APP_PUBLIC_AGENT_ID),
    signatureFormId:
      stringOrNull(context.signatureFormId) ??
      stringOrNull(env.REACT_APP_PUBLIC_SIGNATURE_FORM_ES_ID),
    signatureFormIdEn:
      stringOrNull(context.signatureFormIdEn) ??
      stringOrNull(env.REACT_APP_PUBLIC_SIGNATURE_FORM_EN_ID),
    sendEmail: boolOrDefault(
      context.sendEmail,
      boolOrDefault(env.REACT_APP_PUBLIC_SEND_EMAIL, true)
    ),
    sendSms: boolOrDefault(
      context.sendSms,
      boolOrDefault(env.REACT_APP_PUBLIC_SEND_SMS, false)
    ),
    requireIdentity: boolOrDefault(
      context.requireIdentity,
      boolOrDefault(env.REACT_APP_PUBLIC_REQUIRE_IDENTITY, false)
    ),
    sourceDescription:
      stringOrNull(context.sourceDescription) ??
      stringOrNull(env.REACT_APP_PUBLIC_SOURCE_DESCRIPTION),
    phoneNumber:
      stringOrNull(context.phoneNumber) ??
      stringOrNull(env.REACT_APP_PUBLIC_PHONE_NUMBER),
    whatsappNumber:
      stringOrNull(context.whatsappNumber) ??
      stringOrNull(env.REACT_APP_PUBLIC_WHATSAPP_NUMBER),
    consentTrackingEventName:
      stringOrNull(context.consentTrackingEventName) ??
      stringOrNull(env.REACT_APP_CONSENT_TRACKING_EVENT_NAME) ??
      "ec_form_submit",
    leadEventName:
      stringOrNull(context.leadEventName) ??
      stringOrNull(env.REACT_APP_PUBLIC_LEAD_EVENT_NAME),
    quoteEventName:
      stringOrNull(context.quoteEventName) ??
      stringOrNull(env.REACT_APP_PUBLIC_QUOTE_EVENT_NAME),
    gtmBaseUrl:
      stringOrNull(context.gtmBaseUrl) ?? stringOrNull(env.REACT_APP_GTM_BASE_URL),
    gtmIdEn: stringOrNull(context.gtmIdEn) ?? stringOrNull(env.REACT_APP_GTM_ID_EN),
    gtmIdEs: stringOrNull(context.gtmIdEs) ?? stringOrNull(env.REACT_APP_GTM_ID_ES),
    googleAdsIdEn:
      stringOrNull(context.googleAdsIdEn) ?? stringOrNull(env.REACT_APP_GOOGLE_ADS_ID_EN),
    googleAdsIdEs:
      stringOrNull(context.googleAdsIdEs) ?? stringOrNull(env.REACT_APP_GOOGLE_ADS_ID_ES),
    googleAdsPhoneEn:
      stringOrNull(context.googleAdsPhoneEn) ??
      stringOrNull(env.REACT_APP_GOOGLE_ADS_PHONE_EN),
    googleAdsPhoneEs:
      stringOrNull(context.googleAdsPhoneEs) ??
      stringOrNull(env.REACT_APP_GOOGLE_ADS_PHONE_ES),
    googleAdsLeadConversionIdEn:
      stringOrNull(context.googleAdsLeadConversionIdEn) ??
      stringOrNull(env.REACT_APP_GOOGLE_ADS_LEAD_CONVERSION_ID_EN),
    googleAdsLeadConversionIdEs:
      stringOrNull(context.googleAdsLeadConversionIdEs) ??
      stringOrNull(env.REACT_APP_GOOGLE_ADS_LEAD_CONVERSION_ID_ES),
    googleAdsConsentConversionIdEn:
      stringOrNull(context.googleAdsConsentConversionIdEn) ??
      stringOrNull(env.REACT_APP_GOOGLE_ADS_CONSENT_CONVERSION_ID_EN),
    googleAdsConsentConversionIdEs:
      stringOrNull(context.googleAdsConsentConversionIdEs) ??
      stringOrNull(env.REACT_APP_GOOGLE_ADS_CONSENT_CONVERSION_ID_ES),
    googleAdsQuoteConversionIdEn:
      stringOrNull(context.googleAdsQuoteConversionIdEn) ??
      stringOrNull(env.REACT_APP_GOOGLE_ADS_QUOTE_CONVERSION_ID_EN),
    googleAdsQuoteConversionIdEs:
      stringOrNull(context.googleAdsQuoteConversionIdEs) ??
      stringOrNull(env.REACT_APP_GOOGLE_ADS_QUOTE_CONVERSION_ID_ES),
  };
};
